import { memo, useState } from 'react'
import { TimePicker as MDTimePicker, TimePickerProps as MDTimePickerProps } from '@mui/x-date-pickers/TimePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { isValid } from 'date-fns'
import { mergeSlotProps } from 'utils/slotProps'
import styles from './TimePicker.pcss'
const css = require('styles/variables')

export interface TimePickerProps {
  value: Date
  ampm?: boolean
  disabled?: boolean
  className?: string
  inputClassName?: string
  withActions?: boolean
  MDPickerProps?: Partial<MDTimePickerProps<any>>
  onChange: (date: Date) => void
}

// EXPL: memoized so callers that keep their props stable (memoized `value`, `useCallback` handlers)
// stop re-rendering the picker every time an unrelated part of their state changes.
export const TimePicker = memo(function TimePicker(props: TimePickerProps) {
  const [open, setOpen] = useState(false)
  // EXPL: only `withActions` needs local state — that mode edits a draft until accept/cancel.
  // Otherwise the field renders `props.value` directly, so there is no copy that can drift out of
  // sync with the parent (an effect mirroring the prop used to miss a parent re-supplying the same
  // instant). The draft is dropped on close, which is what discards a cancelled edit.
  const [draft, setDraft] = useState<Date | null>(null)
  const value = props.withActions && draft ? draft : props.value

  // EXPL: the text field is editable, so MUI hands us `null` when it is cleared and an Invalid Date
  // while a segment is half-typed. Callers are typed `(date: Date) => void` and go straight to
  // `.toISOString()`, so both are dropped here and the last valid value stands.
  const isUsable = (next: Date | null): next is Date => Boolean(next) && isValid(next)

  const onChange = (next: Date | null) => {
    if (!isUsable(next)) {
      return
    }
    if (props.withActions) {
      setDraft(next)
    }
    props.onChange(next)
  }

  const onAccept = (next: Date | null) => {
    if (!isUsable(next)) {
      return
    }
    props.onChange(next)
  }

  const openPicker = () => {
    setOpen(true)
  }

  const closePicker = () => {
    setOpen(false)
    setDraft(null)
  }

  const { slotProps: callerSlotProps, ...pickerProps } = props.MDPickerProps || {}

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className={`${props.className || ''} ${styles.box}`} data-testid="time-picker">
        <MDTimePicker
          {...pickerProps}
          open={open}
          ampm={props.ampm}
          value={value}
          closeOnSelect={false}
          timeSteps={{ minutes: 1 }}
          disabled={props.disabled}
          className={`${styles.root} ${props.disabled ? styles.disabled : ''}`}
          slotProps={mergeSlotProps({
            textField: {
              InputProps: {
                classes: { root: styles.pointer, input: props.inputClassName }
              }
            },
            inputAdornment: {
              className: styles['icon-btn']
            },
            actionBar: props.withActions ? {
              actions: ['cancel', 'accept']
            } : undefined,
            desktopPaper: {
              sx: {
                '.MuiClock-root': { position: 'relative', paddingBottom: '8px', marginTop: '32px' },
                '.MuiClock-pmButton, .MuiClock-amButton': { width: '32px', height: '32px' },
                '.MuiClock-pmButton > span, .MuiClock-amButton > span': { lineHeight: '16px' },
                '.MuiClock-pmButton:hover, .MuiClock-amButton:hover': { backgroundColor: css['--pp-green'], color: 'white' },
                '.MuiPickersArrowSwitcher-root': { zIndex: '1' },
                '.MuiMultiSectionDigitalClockSection-item': { fontSize: '12px', width: '56px' },
                '.MuiMultiSectionDigitalClockSection-item.Mui-selected': { color: css['--pp-white'] },
                '.MuiMultiSectionDigitalClockSection-item:after': { display: 'none' },
                '.MuiMultiSectionDigitalClockSection-root': { width: '64px' },
                '.MuiMultiSectionDigitalClockSection-root:last-child:after': { display: 'none' }
              }
            }
          }, callerSlotProps) as any}
          onAccept={onAccept}
          onChange={onChange}
          onOpen={openPicker}
          onClose={closePicker}
        />
      </div>
    </LocalizationProvider>
  )
})

export default TimePicker
